/* Visite 360° Hedayat Music.
   Ce fichier pilote le lecteur : ouverture des vues, déplacements, plan, clavier et,
   en mode travail, la préparation des mises à jour.
   Aucune compilation : le navigateur lit ce fichier tel quel.

   Vocabulaire
   - vue      : un panorama à 360°, pris depuis un point précis du studio (51 en tout)
   - repère   : le rond posé au sol sur lequel on clique pour avancer vers une autre vue
   - yaw      : l'angle gauche-droite du regard, en degrés
   - pitch    : l'angle haut-bas du regard, en degrés
   - hfov     : la largeur du champ de vision, en degrés (plus petit = plus zoomé)
   - tuile    : un petit carré d'image ; le lecteur ne charge que ceux que l'on regarde */
(() => {
  'use strict';

  /* ---------------------------------------------------------------- 1. Données */

  if (window.VISITE_REDIRIGEE || window.VISITE_INTEGRATION.renvoyer()) return;

  const DONNEES = window.TOUR_DATA;
  const CONFIG = Object.assign({ modeTravail: 'sur-demande', ecranAccueil: true, modele: 'MnxHQ6hvpPH' },
    window.VISITE_CONFIG || {});
  // Shopify : un seul réglage pour toutes les images, logo compris.
  const urlImage = nom => {
    const base = (CONFIG.baseImages || 'fichiers/').replace(/\/?$/, '/');
    return new URL(nom, new URL(base, document.baseURI)).href;
  };
  document.querySelectorAll('[data-image-shopify]').forEach(element => {
    element.setAttribute(element.tagName === 'LINK' ? 'href' : 'src', urlImage(element.dataset.imageShopify));
  });
  const $ = id => document.getElementById(id);
  const visite = $('visite');
  const parId = new Map(DONNEES.locations.map(point => [point.id, point]));
  const parNumero = new Map(DONNEES.locations.map(point => [point.number, point]));
  const parametres = new URLSearchParams(location.search);
  const MODE_TRAVAIL = CONFIG.modeTravail !== 'jamais' && parametres.get('mode') === 'travail';
  const mouvementReduit = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const HAUTEUR_CAMERA = 1.52;
  const HFOV_MIN = 45;
  const HFOV_MAX = 110;
  const LETTRES_FACES = ['f', 'r', 'b', 'l', 'u', 'd'];

  let lecteur = null;          // le lecteur Pannellum, créé au premier affichage
  let vueActuelle = null;      // identifiant de la vue affichée
  let jeton = 0;               // numéro du dernier déplacement demandé : les plus anciens sont abandonnés
  let derniereDemande = null;  // pour le bouton « Relancer »
  let deplacementEnCours = false;
  let niveauDuPlan = 1;
  let placement = null;        // en mode travail : numéro de la vue dont on pose le repère
  const reperesPoses = new Map();

  /* ---------------------------------------------------------------- 2. Petits outils */

  const norm = angle => ((angle + 180) % 360 + 360) % 360 - 180;
  const borne = (valeur, mini, maxi) => Math.max(mini, Math.min(maxi, valeur));
  const enDegres = radians => radians * 180 / Math.PI;
  const enRadians = degres => degres * Math.PI / 180;

  function el(balise, classe, texte) {
    const noeud = document.createElement(balise);
    if (classe) noeud.className = classe;
    if (texte !== undefined) noeud.textContent = texte;
    return noeud;
  }

  function svg(balise, attributs) {
    const noeud = document.createElementNS('http://www.w3.org/2000/svg', balise);
    for (const [nom, valeur] of Object.entries(attributs || {})) noeud.setAttribute(nom, valeur);
    return noeud;
  }

  /* Orientation de la capture autour de la verticale. */
  const cap = point => 2 * enDegres(Math.atan2(point.rotation.z, point.rotation.w));

  /* Hauteur du sol sous une vue. La mesure de capture est écartée quand elle est incohérente. */
  function sol(point) {
    const ecart = point.camera.z - point.position.z;
    return Math.abs(ecart - HAUTEUR_CAMERA) <= 0.3 ? point.position.z : point.camera.z - HAUTEUR_CAMERA;
  }

  /* Où regarder, depuis `origine`, pour voir le sol sous `cible`.
     Positions de capture utilisées aussi par scripts/calculer_reperes.py ; ici,
     aucun rabattement à -30° : un point proche reste sous les pieds. */
  function directionVers(origine, cible) {
    const dx = cible.camera.x - origine.camera.x;
    const dy = cible.camera.y - origine.camera.y;
    const distance = Math.hypot(dx, dy);
    return {
      yaw: norm(cap(origine) - enDegres(Math.atan2(dy, dx))),
      pitch: enDegres(Math.atan2(sol(cible) + 0.03 - origine.camera.z, Math.max(distance, 0.05))),
      distance
    };
  }

  /* « Studio A » ne doit jamais se couper entre « Studio » et « A ». */
  const insecable = texte => texte.replace(/ ([A-Z])$/, ' $1');
  const nomDe = point => indications[point.id]?.label || point.label;
  const niveauDe = point => (DONNEES.floors && DONNEES.floors[String(point.floor)]) ||
    (point.floor === 0 ? 'Niveau inférieur' : 'Niveau principal');

  let minuterieAnnonce;
  function annoncer(texte) {
    const zone = $('annonce');
    zone.textContent = texte;
    zone.classList.add('visible');
    clearTimeout(minuterieAnnonce);
    minuterieAnnonce = setTimeout(() => zone.classList.remove('visible'), 4200);
  }

  /* ---------------------------------------------------------------- 3. Indications (mode travail) */

  /* Les indications sont les réglages faits à la main : nom d'une vue, note, angle de départ,
     repères masqués ou déplacés. Elles vivent dans le navigateur (localStorage), puis dans un
     fichier JSON quand on les exporte. */
  const CLE = 'hm-tour-edits-v2';
  const CLE_ANCIENNE = 'hm-tour-edits-v1';
  let stockageDisponible = true;
  let indications = lireIndications();

  function nettoyer(entree) {
    const propre = {};
    if (!entree || typeof entree !== 'object' || Array.isArray(entree)) return propre;
    for (const point of DONNEES.locations) {
      const valeur = entree[point.id];
      if (!valeur || typeof valeur !== 'object') continue;
      const fiche = {};
      if (typeof valeur.label === 'string' && valeur.label.trim() && valeur.label.trim() !== point.label) {
        fiche.label = valeur.label.trim().slice(0, 80);
      }
      if (typeof valeur.note === 'string' && valeur.note.trim()) fiche.note = valeur.note.slice(0, 2000);
      if (valeur.needsUpdate === true) fiche.needsUpdate = true;
      const angle = valeur.angle;
      if (angle && [angle.yaw, angle.pitch, angle.hfov].every(Number.isFinite)) {
        fiche.angle = { yaw: norm(angle.yaw), pitch: borne(angle.pitch, -85, 85), hfov: borne(angle.hfov, HFOV_MIN, HFOV_MAX) };
      }
      if (valeur.links && typeof valeur.links === 'object') {
        const voisins = new Set(point.neighbors.map(id => parId.get(id).number));
        const liens = {};
        for (const [numero, reglage] of Object.entries(valeur.links)) {
          if (!voisins.has(Number(numero)) || !reglage || typeof reglage !== 'object') continue;
          if (reglage.masque === true) liens[numero] = { masque: true };
          else if (Number.isFinite(reglage.yaw) && Number.isFinite(reglage.pitch)) {
            liens[numero] = { yaw: norm(reglage.yaw), pitch: borne(reglage.pitch, -85, 60) };
          } else if (reglage.affiche === true) liens[numero] = { affiche: true };
        }
        if (Object.keys(liens).length) fiche.links = liens;
      }
      if (Object.keys(fiche).length) propre[point.id] = fiche;
    }
    return propre;
  }

  function lireIndications() {
    if (CONFIG.modeTravail === 'jamais') return {};
    try {
      const brut = localStorage.getItem(CLE) || localStorage.getItem(CLE_ANCIENNE);
      return nettoyer(JSON.parse(brut || '{}'));
    } catch (erreur) {
      stockageDisponible = false;
      return {};
    }
  }

  function persister() {
    indications = nettoyer(indications);
    try {
      localStorage.setItem(CLE, JSON.stringify(indications));
      stockageDisponible = true;
    } catch (erreur) {
      stockageDisponible = false;
    }
    afficherEtatEnregistrement(true);
    return stockageDisponible;
  }

  /* ---------------------------------------------------------------- 4. Repères de déplacement */

  /* Liste des repères d'une vue : ceux du catalogue, corrigés par les indications. */
  function reperesDe(point) {
    const reglages = indications[point.id]?.links || {};
    const duCatalogue = new Map((point.links || []).map(lien => [lien.to, lien]));
    const liste = [];
    for (const identifiant of point.neighbors) {
      const cible = parId.get(identifiant);
      const reglage = reglages[cible.number];
      const base = duCatalogue.get(cible.number);
      if (reglage?.masque || (!base && !reglage)) continue;
      const calcul = directionVers(point, cible);
      liste.push({
        cible,
        yaw: reglage?.yaw ?? base?.yaw ?? calcul.yaw,
        pitch: reglage?.pitch ?? (base?.manuel ? base.pitch : calcul.pitch),
        distance: calcul.distance,
        manuel: Number.isFinite(reglage?.yaw) || !!base?.manuel
      });
    }
    return liste;
  }

  /* Dessine un repère. Pannellum fournit le bloc, on le remplit. */
  function dessinerRepere(bloc, repere) {
    const { cible, origine } = repere;
    const changeDEspace = nomDe(cible) !== nomDe(origine);
    const bouton = el('button', 'repere-bouton');
    bouton.type = 'button';
    bouton.setAttribute('aria-label', (changeDEspace ? 'Aller vers l’espace ' : 'Avancer dans l’espace ') + nomDe(cible) +
      (MODE_TRAVAIL ? ', vue ' + cible.number : ''));
    const disque = svg('svg', { class: 'repere-cercle', viewBox: '-32 -32 64 64', 'aria-hidden': 'true' });
    for (const classe of ['anneau-contour', 'anneau-filet', 'anneau-corps', 'anneau-centre']) disque.append(svg('path', { class: classe }));
    bouton.append(disque);
    const etiquette = el('span', 'repere-nom' + (changeDEspace ? ' change-espace' : ''),
      (changeDEspace ? insecable(nomDe(cible)) : 'Avancer') + (MODE_TRAVAIL ? ' · ' + cible.number : ''));
    bloc.dataset.cible = cible.number;
    bloc._repere = repere;
    bloc.append(bouton, etiquette);
    // Les gestes au pointeur sont traités ensemble plus bas, y compris sur un cercle.
    const activer = () => {
      if (placement === null && !deplacementEnCours)
        allerA(cible.id, { garderCap: true, repere });
    };
    // Activer depuis la touche elle-même, sans dépendre d'un clic synthétisé.
    // Empêcher ce clic natif évite aussi un second départ après Entrée / Espace.
    let espaceEnfonce = false;
    bouton.addEventListener('keydown', evenement => {
      if (evenement.key !== 'Enter' && evenement.key !== ' ') return;
      evenement.preventDefault();
      evenement.stopPropagation();
      if (evenement.repeat) return;
      if (evenement.key === 'Enter') activer();
      else espaceEnfonce = true;
    });
    bouton.addEventListener('keyup', evenement => {
      if (evenement.key !== ' ') return;
      evenement.preventDefault();
      evenement.stopPropagation();
      const activerEspace = espaceEnfonce;
      espaceEnfonce = false;
      if (activerEspace) activer();
    });
    bouton.addEventListener('blur', () => { espaceEnfonce = false; });
    // Conserver l'activation sans pointeur des technologies d'assistance.
    bouton.addEventListener('click', evenement => {
      if (evenement.detail === 0 && !evenement.pointerType) activer();
    });
  }

  function hotspotDe(point, repere) {
    return {
      id: 'repere-' + point.number + '-' + repere.cible.number,
      type: 'info',
      yaw: repere.yaw,
      pitch: repere.pitch,
      cssClass: 'repere',
      createTooltipFunc: dessinerRepere,
      createTooltipArgs: Object.assign({ origine: point }, repere)
    };
  }

  /* Repose les repères d'une vue après un réglage. */
  function poserReperes(point) {
    for (const identifiant of reperesPoses.get(point.id) || []) lecteur.removeHotSpot(identifiant, point.id);
    const liste = reperesDe(point).map(repere => hotspotDe(point, repere));
    for (const hotspot of liste) lecteur.addHotSpot(hotspot, point.id);
    reperesPoses.set(point.id, liste.map(hotspot => hotspot.id));
  }

  /* « Montrer le chemin ». Quand aucune flèche n'est dans le champ, ce bouton apparaît en bas de l'image.
     Un clic tourne le regard vers la flèche la plus proche : on n'a jamais à chercher. */
  let reperesActuels = [];
  function rectangleLibre(r, cadre, obstacles) {
    const marge = 8;
    return r.width > 0 && r.height > 0 && r.left >= cadre.left + marge && r.right <= cadre.right - marge &&
      r.top >= cadre.top + marge && r.bottom <= cadre.bottom - marge &&
      !obstacles.some(o => r.left < o.right + marge && r.right > o.left - marge &&
        r.top < o.bottom + marge && r.bottom > o.top - marge);
  }
  function positionEtiquette(bouton, largeur, hauteur, cadre, obstacles) {
    const rectangle = (left, top) => ({ left, top, width: largeur, height: hauteur,
      right: left + largeur, bottom: top + hauteur });
    const centre = bouton.left + (bouton.width - largeur) / 2;
    // Garder le nom près de sa flèche ; changer de côté si une commande gêne.
    return [rectangle(centre, bouton.top + 8 - hauteur),
      rectangle(centre, bouton.bottom + 8),
      rectangle(bouton.right + 8, bouton.top + (bouton.height - hauteur) / 2),
      rectangle(bouton.left - largeur - 8, bouton.top + (bouton.height - hauteur) / 2)]
      .find(r => rectangleLibre(r, cadre, obstacles));
  }
  function flecheDansLeChamp() {
    const cadre = $('panorama').getBoundingClientRect();
    // L'aide passagère s'efface devant les flèches, jamais l'inverse.
    // « Montrer le chemin » dépend des cercles visibles : il ne doit jamais les masquer.
    // Les autres commandes de la barre du bas et le crédit restent des obstacles.
    const obstacles = [...visite.querySelectorAll('#commerce, #partage-menu, .entete, .commandes, .bas button, .plan, .panneau-espaces, .atelier')]
      .filter(e => e.id !== 'chemin' && !e.hidden && getComputedStyle(e).display !== 'none')
      .map(e => e.getBoundingClientRect()).filter(r => r.width > 0 && r.height > 0);
    const astuce = $('astuce'), aide = astuce.getBoundingClientRect();
    const etats = [...$('panorama').querySelectorAll('.repere')].map(bloc => {
      const bouton = bloc.querySelector('button'), etiquette = bloc.querySelector('.repere-nom');
      if (bloc._repere) dessinerAnneau(bloc.querySelector('.repere-cercle'), formeDuCercle(bloc._repere.yaw, bloc._repere.pitch, { yaw: lecteur.getYaw(), pitch: lecteur.getPitch(), hfov: lecteur.getHfov() }, cadre.width, cadre.height));
      const r = bouton.getBoundingClientRect();
      const libre = getComputedStyle(bloc).visibility === 'visible' && rectangleLibre(r, cadre, obstacles);
      const taille = etiquette && etiquette.getBoundingClientRect();
      const nom = taille && positionEtiquette(r, taille.width, taille.height, cadre, obstacles);
      return { bloc, bouton, etiquette, r, nom, libre };
    });
    // Ne pas toucher à visibility, que Pannellum utilise pour les repères derrière la caméra.
    for (const { bloc, bouton, etiquette, r, nom, libre } of etats) {
      if (!libre && bloc.contains(document.activeElement)) $('panorama').focus({ preventScroll: true });
      bloc.classList.toggle('repere-masque', !libre);
      bouton.tabIndex = libre ? 0 : -1;
      bloc.setAttribute('aria-hidden', String(!libre));
      if (etiquette) {
        etiquette.classList.toggle('etiquette-masquee', !nom);
        if (nom) Object.assign(etiquette.style, { left: (nom.left - r.left) + 'px',
          top: (nom.top - r.top) + 'px', bottom: 'auto', transform: 'none' });
      }
    }
    astuce.classList.toggle('astuce-effacee', etats.some(e => e.libre &&
      (!rectangleLibre(e.r, cadre, [aide]) || (e.nom && !rectangleLibre(e.nom, cadre, [aide])))));
    return etats.some(e => e.libre);
  }
  function montrerLeChemin() {
    const yaw = lecteur.getYaw();
    const proche = reperesActuels.map(repere => ({ repere, ecart: Math.abs(norm(repere.yaw - yaw)) }))
      .sort((a, b) => a.ecart - b.ecart)[0];
    ouvrirContacts(false);
    if (proche) lecteur.lookAt(proche.repere.pitch, proche.repere.yaw, lecteur.getHfov(), mouvementReduit ? false : 550);
  }
  let dernierControleChemin = 0;
  (function surveillerLeChemin(instant = 0) {
    if (instant - dernierControleChemin >= 80 && lecteur && vueActuelle && visite.dataset.etat === 'visite') {
      dernierControleChemin = instant;
      const perdu = lecteur.isLoaded() && placement === null && reperesActuels.length > 0 && !flecheDansLeChamp();
      if ($('chemin').hidden === perdu) $('chemin').hidden = !perdu;
      actualiserCurseur();
    }
    requestAnimationFrame(surveillerLeChemin);
  })();

  /* ---------------------------------------------------------------- 5. Images */

  /* Mode aperçu (config.js, apercu : true) : une page d'essai privée, limitée en nombre de fichiers.
     Chaque vue y tient en six images entières, moins fines, au lieu de tuiles. La visite normale n'est pas concernée. */
  const APERCU = CONFIG.apercu === true;
  const urlTuile = (point, niveau, lettre, ligne, colonne) =>
    `${point.tuiles.dossier}/${niveau}/${lettre}${ligne}_${colonne}.jpg?v=${point.tuiles.revision}`;
  /* Dans l'aperçu, une vue retouchée reçoit de nouveaux noms de fichiers (suffixes écrits par
     preparer_apercu_prive.py) : un navigateur ne peut pas resservir l'ancienne image à la place. */
  const suffixe = (point, quoi) => (APERCU && point.tuiles.apercu && point.tuiles.apercu[quoi]) || '';
  const urlFace = (point, lettre) => urlImage(point.tuiles.imagesShopify[lettre]);
  const urlVignette = point => urlImage(point.tuiles.imagesShopify.vignette);
  const urlAffiche = (point, portrait = false) =>
    urlImage(point.tuiles.imagesShopify[portrait ? 'affiche-portrait' : 'affiche']);

  /* Charge les six faces en basse définition d'une vue. Deux usages : la vue s'affiche d'un
     coup au lieu d'apparaître morceau par morceau, et une image manquante est détectée avant
     de quitter la vue en cours. */
  const prechargements = new Map();
  function precharger(point, discret = false) {
    if (prechargements.has(point.id)) {
      const enCours = prechargements.get(point.id);
      if (!discret) {
        enCours.discret = false;
        enCours.images.forEach(image => { image.fetchPriority = 'high'; });
      }
      return enCours.promesse;
    }
    const enCours = { discret, images: [], annulations: [] };
    const promesse = Promise.all(LETTRES_FACES.map(lettre => new Promise((resoudre, rejeter) => {
      const image = new Image();
      enCours.images.push(image);
      let fini = false;
      const finir = erreur => {
        if (fini) return;
        fini = true;
        clearTimeout(delai);
        image.onload = image.onerror = null;
        if (erreur) { image.removeAttribute('src'); rejeter(erreur); }
        else resoudre();
      };
      const delai = setTimeout(() => finir(new Error('délai dépassé')), 20000);
      enCours.annulations.push(() => finir(new Error('préparation interrompue')));
      image.crossOrigin = 'anonymous';
      image.fetchPriority = discret ? 'low' : 'high';
      image.onload = () => finir();
      image.onerror = () => finir(new Error('image manquante : ' + image.src));
      image.src = APERCU ? urlFace(point, lettre) : urlTuile(point, 1, lettre, 0, 0);
    })));
    enCours.promesse = promesse;
    enCours.annuler = () => {
      enCours.annulations.forEach(annuler => annuler());
      if (prechargements.get(point.id) === enCours) prechargements.delete(point.id);
    };
    prechargements.set(point.id, enCours);
    promesse.then(() => {
      enCours.images = []; // le cache du navigateur garde les fichiers ; pas 51 panoramas décodés en mémoire
      enCours.annulations = [];
      enCours.discret = false;
    }, () => enCours.annuler()); // échec ou interruption : un prochain clic pourra réessayer
    return promesse;
  }

  /* Même jeu d'images dans l'aperçu : aucun fichier supplémentaire. Deux voisines à la fois,
     d'abord devant le regard. Au clic, les autres préparations cèdent la connexion à la cible. */
  let preparationVoisines = 0;
  const economieDeDonnees = () => navigator.connection &&
    (navigator.connection.saveData || ['slow-2g', '2g'].includes(navigator.connection.effectiveType));
  function interrompreVoisines(cible) {
    preparationVoisines++;
    for (const [id, enCours] of prechargements) {
      if (enCours.discret && id !== cible) enCours.annuler();
    }
  }
  function prechargerVoisines(point) {
    interrompreVoisines();
    if (economieDeDonnees() || document.hidden) return;
    const tour = preparationVoisines;
    setTimeout(() => {
      const autorise = () => tour === preparationVoisines && vueActuelle === point.id &&
        !document.hidden && !economieDeDonnees();
      if (!autorise()) return;
      const file = reperesDe(point);
      const suivant = async () => {
        while (autorise() && file.length) {
          const regard = lecteur.getYaw();
          file.sort((a, b) => Math.abs(norm(a.yaw - regard)) - Math.abs(norm(b.yaw - regard)));
          const repere = file.shift();
          try { await precharger(repere.cible, true); } catch (_) { /* discret : aucune alerte */ }
        }
      };
      suivant(); suivant();
    }, 120); // laisse la première image s'afficher avant les demandes de fond
  }
  const reprendrePreparation = () => {
    interrompreVoisines();
    if (!document.hidden && !deplacementEnCours && lecteur && lecteur.isLoaded() && vueActuelle) {
      prechargerVoisines(parId.get(vueActuelle));
    }
  };
  document.addEventListener('visibilitychange', reprendrePreparation);
  navigator.connection?.addEventListener('change', reprendrePreparation);

  /* ---------------------------------------------------------------- 6. Lecteur et déplacements */

  /* Champ de vision adapté à la forme de l'écran : sur un téléphone tenu debout,
     un champ trop large déforme l'image. */
  function hfovAdapte(souhaite) {
    const zone = $('panorama');
    const ratio = zone.clientWidth / Math.max(1, zone.clientHeight);
    const limite = 2 * enDegres(Math.atan(Math.tan(enRadians(50)) * ratio));   // 100° de haut au plus
    return borne(Math.min(souhaite, limite), 55, HFOV_MAX);
  }

  function angleDeDepart(point) {
    const regle = indications[point.id]?.angle;
    // Même cadrage que l'affiche portrait ; les autres vues et les liens directs restent libres.
    const cadrage = point.id === DONNEES.start && matchMedia('(max-aspect-ratio: 1/1)').matches
      ? DONNEES.accueil?.portrait : null;
    return {
      yaw: regle?.yaw ?? cadrage?.yaw ?? point.yaw,
      pitch: regle?.pitch ?? cadrage?.pitch ?? point.pitch,
      hfov: hfovAdapte(regle?.hfov ?? cadrage?.hfov ?? point.hfov)
    };
  }

  function sceneDe(point) {
    const liste = reperesDe(point).map(repere => hotspotDe(point, repere));
    reperesPoses.set(point.id, liste.map(hotspot => hotspot.id));
    if (APERCU) {
      return { type: 'cubemap', cubeMap: LETTRES_FACES.map(lettre => urlFace(point, lettre)),
        yaw: point.yaw, pitch: point.pitch, hfov: point.hfov, hotSpots: liste };
    }
    return {
      type: 'multires',
      multiRes: {
        basePath: point.tuiles.dossier,
        path: '/%l/%s%y_%x',
        fallbackPath: '/1/%s0_0',
        extension: 'jpg?v=' + point.tuiles.revision,   // le « ?v= » force le navigateur à reprendre une image retouchée
        tileResolution: 512,
        maxLevel: 3,
        cubeResolution: 2048
      },
      yaw: point.yaw,
      pitch: point.pitch,
      hfov: point.hfov,
      hotSpots: liste
    };
  }

  /* Un lien peut imposer un angle précis : ?yaw=20&pitch=60&hfov=90#point-25.
     Pratique pour montrer un détail, par exemple un plafond retouché. */
  function angleDeLAdresse() {
    const lu = nom => parametres.has(nom) && parametres.get(nom) !== '' ? Number(parametres.get(nom)) : NaN;
    const angle = {};
    if (Number.isFinite(lu('yaw'))) angle.yaw = norm(lu('yaw'));
    if (Number.isFinite(lu('pitch'))) angle.pitch = borne(lu('pitch'), -90, 90);
    if (Number.isFinite(lu('hfov'))) angle.hfov = borne(lu('hfov'), HFOV_MIN, HFOV_MAX);
    return angle;
  }

  function creerLecteur(identifiant) {
    const depart = Object.assign(angleDeDepart(parId.get(identifiant)), angleDeLAdresse());
    const scenes = Object.fromEntries(DONNEES.locations.map(point => [point.id, sceneDe(point)]));
    Object.assign(scenes[identifiant], depart);
    lecteur = pannellum.viewer('panorama', {
      default: {
        firstScene: identifiant,
        autoLoad: true,
        showControls: false,
        sceneFadeDuration: 0, // le chevauchement est animé ci-dessous, sans pause du lecteur
        minHfov: HFOV_MIN,
        maxHfov: HFOV_MAX,
        multiResMinHfov: true,
        mouseZoom: true,
        keyboardZoom: false,
        disableKeyboardCtrl: true,   // le clavier est géré plus bas, pour toute la page
        friction: 0.12,
        escapeHTML: true,
        strings: {
          loadingLabel: 'Chargement…',
          bylineLabel: '',
          noPanoramaError: 'Cette vue est indisponible.',
          fileAccessError: 'Cette vue ne peut pas être chargée. Vérifie ta connexion et réessaie.',
          genericWebGLError: 'La visite ne peut pas s’afficher ici. Essaie avec un autre navigateur.',
          unknownError: 'Erreur inattendue.'
        }
      },
      scenes
    });
    lecteur.on('scenechange', surChangementDeVue);
    lecteur.on('load', surVueChargee);
    lecteur.on('error', () => { terminerTransition(); montrerAlerte(); });
    surChangementDeVue(identifiant);
    // La première vue peut être prête avant même que l'écoute ci-dessus soit posée.
    if (lecteur.isLoaded()) surVueChargee();
  }

  /* Va à une vue.
     garderCap : on garde la direction du regard (déplacement à pied) ; sinon on prend l'angle de départ de la vue.
     repere    : le repère cliqué, pour le petit mouvement d'avancée. */
  // Un seul mouvement : ancienne image agrandie et déplacée, nouvelle vue animée
  // sous elle. Pas de sérialisation « fin du zoom, chargement, fin du fondu ».
  let transitionVisite = null;
  const douceur = t => { t = borne(t, 0, 1); return t * t * (3 - 2 * t); };
  function progressionTransition(debut, prete, instant) {
    const temps = Math.max(0, instant - debut);
    const fondu = prete === null ? 0 : douceur((instant - Math.max(debut + 220, prete)) / 780);
    return { echelle: 1 + 0.24 * (1 - Math.exp(-Math.pow(temps / 650, 2))), fondu };
  }
  function terminerTransition() {
    if (transitionVisite) {
      cancelAnimationFrame(transitionVisite.image);
      transitionVisite.rendu.style.removeProperty('transform');
    }
    transitionVisite = null;
    const image = $('passage-image');
    image.hidden = true;
    image.style.removeProperty('transform');
    image.style.removeProperty('opacity');
    image.width = image.height = 1; // libérer la copie de l'image précédente
    visite.classList.remove('en-deplacement');
    deplacementEnCours = false;
    actualiserCurseur();
  }
  function commencerTransition(cible, repere, hfov) {
    if (mouvementReduit || !repere || !lecteur.isLoaded()) return null;
    const image = $('passage-image'), zone = $('panorama').getBoundingClientRect();
    const renduArrivee = $('panorama').querySelector('.pnlm-render-container');
    if (!renduArrivee) return null;
    try {
      const rendu = lecteur.getRenderer();
      rendu.render(enRadians(lecteur.getPitch()), enRadians(lecteur.getYaw()), enRadians(lecteur.getHfov()));
      const source = rendu.getCanvas();
      // Copie directe en mémoire, sans compression ni téléchargement d'une image.
      const ratio = Math.min(1.5, devicePixelRatio || 1, 1920 / zone.width);
      image.width = Math.round(zone.width * ratio);
      image.height = Math.round(zone.height * ratio);
      image.getContext('2d', { alpha: false }).drawImage(source, 0, 0, image.width, image.height);
    } catch (_) { image.width = image.height = 1; return null; }
    const origine = parId.get(vueActuelle);
    const t = { rendu: renduArrivee, cible: cible.id, debut: performance.now(), prete: null, image: null,
      yaw: norm(repere.yaw + cap(cible) - cap(origine)),
      pitch: borne(lecteur.getPitch() * 0.5 + repere.pitch * 0.15, -25, 12),
      hfov: hfovAdapte(hfov), departHfov: hfovAdapte(hfov) * 0.82 };
    // Le pivot reste dans l'image pour éviter de découvrir un bord pendant le zoom.
    const x = borne(50 + norm(repere.yaw - lecteur.getYaw()) / lecteur.getHfov() * 80, 15, 85);
    const y = borne(50 - (repere.pitch - lecteur.getPitch()) / 100 * 80, 20, 85);
    image.style.transformOrigin = x + '% ' + y + '%';
    image.style.opacity = '1'; image.style.transform = 'scale(1)'; image.hidden = false;
    // Charger au champ final, puis recadrer la couche affichée : le mouvement reste
    // visible même si le moteur ne redessine pas une photo immobile à chaque image.
    t.echelleArrivee = Math.tan(enRadians(t.hfov / 2)) / Math.tan(enRadians(t.departHfov / 2));
    t.rendu.style.transform = 'scale(' + t.echelleArrivee + ')';
    visite.classList.add('en-deplacement');
    lecteur.stopMovement();
    transitionVisite = t;
    const animer = instant => {
      if (transitionVisite !== t) return;
      const p = progressionTransition(t.debut, t.prete, instant);
      image.style.transform = 'scale(' + p.echelle + ')';
      image.style.opacity = String(1 - p.fondu);
      t.rendu.style.transform = 'scale(' + (1 + (t.echelleArrivee - 1) * (1 - p.fondu)) + ')';
      if (p.fondu >= 1) { terminerTransition(); return; }
      t.image = requestAnimationFrame(animer);
    };
    t.image = requestAnimationFrame(animer);
    return t;
  }

  async function allerA(identifiant, options = {}) {
    const cible = parId.get(identifiant);
    if (!cible) return;
    derniereDemande = [identifiant, options];
    if (visite.dataset.etat === 'accueil') { demarrer(identifiant); return; }
    if (!lecteur) return;
    if (identifiant === vueActuelle && !transitionVisite) {
      if (!options.garderCap) recentrer();
      return;
    }
    const monJeton = ++jeton;
    terminerTransition();
    const origine = parId.get(vueActuelle), hfovAvant = lecteur.getHfov();
    const regard = { yaw: lecteur.getYaw(), pitch: lecteur.getPitch() };
    deplacementEnCours = true;
    effacerCurseur();
    interrompreVoisines(identifiant);
    masquerAlerte();
    montrerChargement(cible);
    const transition = commencerTransition(cible, options.repere, hfovAvant);
    try { await precharger(cible); }
    catch (_) {
      if (monJeton === jeton) { terminerTransition(); montrerAlerte(cible); }
      return;
    }
    if (monJeton !== jeton) return;
    const depart = angleDeDepart(cible);
    if (transition) lecteur.loadScene(identifiant, transition.pitch, transition.yaw, transition.hfov);
    else if (options.garderCap) lecteur.loadScene(identifiant, borne(regard.pitch, -25, 12),
      norm((options.repere?.yaw ?? regard.yaw) + cap(cible) - cap(origine)), hfovAdapte(hfovAvant));
    else lecteur.loadScene(identifiant, depart.pitch, depart.yaw, depart.hfov);
  }

  function surChangementDeVue(identifiant) {
    vueActuelle = identifiant;
    const point = parId.get(identifiant);
    niveauDuPlan = point.floor;
    window.VISITE_INTEGRATION.actualiserAdresse(point.number);
    afficherVue();
  }

  function surVueChargee() {
    if (transitionVisite?.cible === vueActuelle) transitionVisite.prete = performance.now();
    else deplacementEnCours = false;
    const point = parId.get(vueActuelle);
    masquerChargement();
    masquerAlerte();
    $('annonce').textContent = insecable(nomDe(point)) + ', ' + niveauDe(point).toLowerCase() + '.';
    prechargerVoisines(point);
    proposerAstuce();
    actualiserCurseur();
    if (!espacesInitialises) {
      espacesInitialises = true;
      if (!MODE_TRAVAIL && matchMedia('(min-width: 1024px) and (pointer: fine)').matches) ouvrirEspaces(true);
    }
  }

  function recentrer() {
    if (!lecteur) return;
    visee.instant = 0;
    const depart = angleDeDepart(parId.get(vueActuelle));
    lecteur.lookAt(depart.pitch, depart.yaw, depart.hfov, mouvementReduit ? false : 450);
  }

  /* Chargement : le message n'apparaît que si l'attente se voit. */
  let minuterieChargement;
  function montrerChargement(point) {
    clearTimeout(minuterieChargement);
    $('chargement-texte').textContent = insecable(nomDe(point)) + ' : chargement…';
    minuterieChargement = setTimeout(() => { $('chargement').hidden = false; }, 350);
  }
  function masquerChargement() {
    clearTimeout(minuterieChargement);
    $('chargement').hidden = true;
  }

  function montrerAlerte(point) {
    masquerChargement();
    $('alerte-texte').textContent = point
      ? insecable(nomDe(point)) + ' : vérifie ta connexion, puis relance le chargement.'
      : 'Vérifie ta connexion, puis relance le chargement.';
    $('alerte').hidden = false;
    $('alerte-relancer').focus({ preventScroll: true });
  }
  function masquerAlerte() { $('alerte').hidden = true; }

  /* Premier affichage : on quitte l'écran d'accueil et on crée le lecteur. */
  async function demarrer(identifiant) {
    const point = parId.get(identifiant) || parId.get(DONNEES.start);
    const accueil = $('accueil');
    const depuisLAccueil = !accueil.hidden && visite.dataset.etat === 'accueil';
    visite.dataset.etat = 'visite';
    accueil.classList.add('sortie');             // le texte s'efface, l'affiche reste encore
    if (!depuisLAccueil) accueil.hidden = true;
    montrerChargement(point);
    derniereDemande = [point.id, {}];
    try {
      await precharger(point);
    } catch (erreur) {
      visite.dataset.etat = 'accueil';   // « Relancer » repassera par ici
      accueil.classList.remove('sortie');
      montrerAlerte(point);
      return;
    }
    creerLecteur(point.id);
    // L'affiche s'efface une fois la première image en place : on passe de l'une à l'autre sans écran noir.
    setTimeout(() => {
      accueil.classList.add('efface');
      setTimeout(() => { accueil.hidden = true; }, mouvementReduit ? 0 : 520);
    }, mouvementReduit ? 0 : 260);
    $('panorama').focus({ preventScroll: true });
  }

  /* ---------------------------------------------------------------- 7. Affichage autour de l'image */

  function afficherEntete() {
    const point = parId.get(vueActuelle);
    $('nom-espace').textContent = insecable(nomDe(point));
    $('niveau').textContent = niveauDe(point);
    document.title = nomDe(point) + ' · Visite à 360° · Hedayat Music';
    // Numéro réservé à la préparation interne.
    if (MODE_TRAVAIL) {
      $('numero-vue').hidden = false;
      $('numero-vue').textContent = 'Vue ' + point.number + (indications[point.id]?.needsUpdate ? ' · à actualiser' : '');
      $('numero-vue').classList.toggle('a-actualiser', !!indications[point.id]?.needsUpdate);
    }
  }

  function afficherVue() {
    afficherEntete();
    actualiserCommerce();
    afficherEspaces();
    reperesActuels = reperesDe(parId.get(vueActuelle));
    dessinerPlan();
    if (MODE_TRAVAIL) remplirAtelier();
  }

  function actualiserCommerce() {
    const config = CONFIG.commerce;
    if (!config) { $('commerce').hidden = true; return; }
    $('note-google').textContent = config.noteGoogle;
    $('avis-google').href = config.avisGoogle;
    $('nombre-avis').textContent = Number.isInteger(config.nombreAvis) && config.nombreAvis >= 0 ? config.nombreAvis + ' avis' : 'Avis Google';
    $('avis-google').setAttribute('aria-label', config.noteGoogle + ' sur Google, ' + $('nombre-avis').textContent);
    $('reserver-seance').href = window.VISITE_COMMERCE.lienSeance(parId.get(vueActuelle), config);
    $('rendez-vous').href = config.rendezVous;
    $('appeler').href = config.telephone;

  }

  function ouvrirContacts(ouvert) {
    if (ouvert) ouvrirPartage(false);
    $('contacts').hidden = !ouvert;
    $('contacts-ouvrir').setAttribute('aria-expanded', String(ouvert));
  }

  /* Liste des espaces, ouverte par le bouton « Espaces ». */
  function afficherEspaces() {
    const actuel = vueActuelle ? nomDe(parId.get(vueActuelle)) : null;
    $('espaces').replaceChildren(...DONNEES.destinations.map(numero => {
      const point = parNumero.get(numero);
      const bouton = el('button', 'espace');
      bouton.type = 'button';
      if (nomDe(point) === actuel) bouton.setAttribute('aria-current', 'true');
      const image = el('img');
      image.src = urlVignette(point);
      image.alt = '';
      image.width = 320;
      image.height = 180;
      bouton.dataset.vue = numero;
      image.loading = 'lazy';
      image.decoding = 'async';
      const texte = el('span', 'espace-texte');
      texte.append(el('strong', '', insecable(nomDe(point))));
      const fleche = el('span', 'espace-fleche', '↗');
      fleche.setAttribute('aria-hidden', 'true');
      bouton.append(image, texte, fleche);
      if (nomDe(point) === actuel) bouton.append(el('span', 'espace-ici', 'Tu es ici'));
      bouton.addEventListener('click', () => { if (!panneauEspacesPermanent()) ouvrirEspaces(false); allerA(point.id); });
      return bouton;
    }));
  }

  const panneauEspacesPermanent = () => matchMedia('(min-width: 1024px) and (pointer: fine)').matches;
  let espacesInitialises = false;
  function ouvrirEspaces(ouvert) {
    if (ouvert) { ouvrirPlan(false); ouvrirContacts(false); effacerCurseur(); }
    $('espaces-panneau').hidden = !ouvert;
    visite.classList.toggle('espaces-ouverts', ouvert);
    $('espaces-ouvrir').setAttribute('aria-expanded', String(ouvert));
  }

  /* Plan de repérage : des positions et des liaisons, pas des murs. */
  const cadre = (() => {
    const xs = DONNEES.locations.map(p => p.camera.x), ys = DONNEES.locations.map(p => p.camera.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    const echelle = Math.min(316 / (maxX - minX), 160 / (maxY - minY));
    const margeX = (360 - (maxX - minX) * echelle) / 2, margeY = (196 - (maxY - minY) * echelle) / 2 - 6;
    return point => [margeX + (point.camera.x - minX) * echelle, 196 - margeY - (point.camera.y - minY) * echelle];
  })();

  function dessinerPlan() {
    const niveaux = [...new Set(DONNEES.locations.map(p => p.floor))].sort((a, b) => b - a);
    $('plan-niveaux').replaceChildren(...niveaux.map(niveau => {
      const bouton = el('button', 'onglet', niveauDe({ floor: niveau }));
      bouton.type = 'button';
      bouton.setAttribute('aria-pressed', String(niveau === niveauDuPlan));
      bouton.addEventListener('click', () => { niveauDuPlan = niveau; dessinerPlan(); });
      return bouton;
    }));
    const dessin = $('plan-dessin');
    const points = DONNEES.locations.filter(p => p.floor === niveauDuPlan);
    const traits = svg('g', { class: 'plan-traits' });
    const dejaTrace = new Set();
    for (const point of points) {
      for (const repere of reperesDe(point)) {
        const cle = [point.number, repere.cible.number].sort((a, b) => a - b).join('-');
        if (dejaTrace.has(cle)) continue;
        dejaTrace.add(cle);
        const [x1, y1] = cadre(point), [x2, y2] = cadre(repere.cible);
        // Une liaison vers l'autre niveau (l'escalier) est tracée en pointillés.
        traits.append(svg('line', { x1, y1, x2, y2, class: repere.cible.floor === niveauDuPlan ? '' : 'vers-autre-niveau' }));
      }
    }
    const noms = svg('g', { class: 'plan-noms', 'aria-hidden': 'true' });
    const groupes = new Map();
    for (const point of points) {
      const nom = nomDe(point);
      if (!groupes.has(nom)) groupes.set(nom, []);
      groupes.get(nom).push(cadre(point));
    }
    for (const [nom, positions] of groupes) {
      if (/^Passage/.test(nom)) continue;   // les passages ne sont pas nommés : le plan reste lisible
      const x = positions.reduce((somme, p) => somme + p[0], 0) / positions.length;
      const y = Math.min(...positions.map(p => p[1])) - 9;
      const texte = svg('text', { x: borne(x, 30, 330), y: Math.max(10, y), 'text-anchor': 'middle' });
      texte.textContent = insecable(nom);
      noms.append(texte);
    }
    const pastilles = svg('g', { class: 'plan-points' });
    for (const point of points) {
      const [x, y] = cadre(point);
      const groupe = svg('g', { role: 'button', tabindex: '0', class: point.id === vueActuelle ? 'ici' : '' });
      const etiquette = insecable(nomDe(point)) + (MODE_TRAVAIL ? ', vue ' + point.number : '') +
        (point.id === vueActuelle ? ' : tu es ici' : '');
      groupe.setAttribute('aria-label', etiquette);
      const titre = svg('title');
      titre.textContent = etiquette;
      groupe.append(titre, svg('circle', { cx: x, cy: y, r: 11, class: 'zone' }),
        svg('circle', { cx: x, cy: y, r: point.id === vueActuelle ? 6 : 4, class: 'point' }));
      if (MODE_TRAVAIL) {
        const numero = svg('text', { x: x + 6, y: y - 6, class: 'numero' });
        numero.textContent = point.number;
        groupe.append(numero);
      }
      const ouvrir = () => allerA(point.id, { garderCap: true });
      groupe.addEventListener('click', ouvrir);
      groupe.addEventListener('keydown', evenement => {
        if (evenement.key === 'Enter' || evenement.key === ' ') { evenement.preventDefault(); ouvrir(); }
      });
      pastilles.append(groupe);
    }
    dessin.replaceChildren(traits, svg('path', { id: 'plan-regard', class: 'plan-regard' }), pastilles, noms);
    dessinerRegard();
  }

  /* Le petit éventail qui montre dans quelle direction on regarde. */
  function dessinerRegard() {
    const trace = $('plan-regard');
    const point = parId.get(vueActuelle);
    if (!trace || !point || !lecteur || point.floor !== niveauDuPlan) { if (trace) trace.setAttribute('d', ''); return; }
    const [x, y] = cadre(point);
    const direction = enRadians(cap(point) - lecteur.getYaw());
    const demi = enRadians(borne(lecteur.getHfov(), 40, 110) / 2);
    const rayon = 30;
    const bord = angle => [x + rayon * Math.cos(angle), y - rayon * Math.sin(angle)];
    const [ax, ay] = bord(direction + demi), [bx, by] = bord(direction - demi);
    trace.setAttribute('d', `M${x},${y} L${ax.toFixed(1)},${ay.toFixed(1)} A${rayon},${rayon} 0 0 1 ${bx.toFixed(1)},${by.toFixed(1)} Z`);
  }

  let suiviDuRegard;
  function ouvrirPlan(ouvert) {
    if (ouvert && !panneauEspacesPermanent()) $('espaces-panneau').hidden || ouvrirEspaces(false);
    $('plan').hidden = !ouvert;
    $('plan-ouvrir').setAttribute('aria-expanded', String(ouvert));
    clearInterval(suiviDuRegard);
    if (ouvert) {
      niveauDuPlan = parId.get(vueActuelle).floor;
      dessinerPlan();
      suiviDuRegard = setInterval(dessinerRegard, 120);
    }
  }

  /* Astuce de prise en main, montrée une seule fois par visite. */
  function proposerAstuce() {
    let dejaVue = false;
    try { dejaVue = sessionStorage.getItem('hm-visite-astuce') === '1'; } catch (erreur) { /* stockage fermé : on affiche */ }
    if (dejaVue || proposerAstuce.fait) return;
    proposerAstuce.fait = true;
    const tactile = matchMedia('(pointer: coarse)').matches;
    const astuce = $('astuce');
    astuce.textContent = tactile
      ? 'Fais glisser pour regarder autour de toi. Touche le sol ou un cercle pour avancer.'
      : 'Fais glisser pour regarder autour de toi. Clique sur le sol ou un cercle pour avancer.';
    astuce.hidden = false;
    const retirer = () => {
      astuce.hidden = true;
      try { sessionStorage.setItem('hm-visite-astuce', '1'); } catch (erreur) { /* sans gravité */ }
      $('panorama').removeEventListener('pointerdown', retirer);
    };
    $('panorama').addEventListener('pointerdown', retirer);
    setTimeout(retirer, 9000);
  }

  /* Plein écran, quand le navigateur le permet. Sur iPhone, Safari le réserve aux vidéos :
     le bouton est alors masqué. */
  const pleinEcranPossible = !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);
  const enPleinEcran = () => document.fullscreenElement || document.webkitFullscreenElement;

  function preparerPleinEcran() {
    const bouton = $('plein-ecran');
    if (!pleinEcranPossible) { bouton.hidden = true; return; }
    const suivre = () => {
      const actif = !!enPleinEcran();
      bouton.setAttribute('aria-pressed', String(actif));
      bouton.setAttribute('aria-label', actif ? 'Quitter le plein écran' : 'Afficher en plein écran');
      bouton.title = actif ? 'Quitter le plein écran' : 'Plein écran';
      visite.classList.toggle('plein-ecran', actif);
      if (lecteur) lecteur.resize();
    };
    document.addEventListener('fullscreenchange', suivre);
    document.addEventListener('webkitfullscreenchange', suivre);
  }

  async function basculerPleinEcran() {
    if (!pleinEcranPossible) {
      return;
    }
    try {
      if (enPleinEcran()) await (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      else await (visite.requestFullscreen || visite.webkitRequestFullscreen).call(visite);
    } catch (erreur) {
      annoncer('Le plein écran est réservé à un autre réglage de ton navigateur.');
    }
  }

  /* Visée demandée au clavier ou aux boutons. Si l'on appuie plusieurs fois de suite, chaque appui
     part de la visée précédente et non de l'image encore en mouvement : dix appuis font dix pas. */
  const visee = { yaw: 0, pitch: 0, hfov: 0, instant: 0 };
  function viser(deltaYaw, deltaPitch, deltaHfov) {
    if (!lecteur) return;
    const maintenant = performance.now();
    if (maintenant - visee.instant > 450) {
      visee.yaw = lecteur.getYaw(); visee.pitch = lecteur.getPitch(); visee.hfov = lecteur.getHfov();
    }
    visee.instant = maintenant;
    visee.yaw += deltaYaw;
    visee.pitch = borne(visee.pitch + deltaPitch, -85, 85);
    visee.hfov = borne(visee.hfov + deltaHfov, HFOV_MIN, HFOV_MAX);
    lecteur.lookAt(visee.pitch, visee.yaw, visee.hfov, mouvementReduit ? false : 200);
  }

  function zoomer(sens) { viser(0, 0, -sens * 13); }

  function ouvrirInfos(ouvert) {
    const boite = $('infos');
    if (ouvert) { ouvrirPartage(false); ouvrirContacts(false); effacerCurseur(); }
    if (ouvert && !boite.open) boite.showModal ? boite.showModal() : boite.setAttribute('open', '');
    if (ouvert) { boite.scrollTop = 0; $('infos-fermer').focus({ preventScroll: true }); }
    if (!ouvert && boite.open) boite.close ? boite.close() : boite.removeAttribute('open');
  }

  // Boucle explicite : certains navigateurs passent par leur barre d'adresse
  // entre le dernier contrôle d'un dialogue et le premier.
  function garderFocusDansAide(evenement) {
    if (evenement.key !== 'Tab') return;
    const controles = [...$('infos').querySelectorAll('button, a[href], input, select, textarea, [tabindex]')]
      .filter(e => !e.disabled && e.tabIndex >= 0 && e.getClientRects().length);
    const premier = controles[0], dernier = controles[controles.length - 1];
    if (!premier) { evenement.preventDefault(); return; }
    const actif = document.activeElement;
    if (!controles.includes(actif) || (evenement.shiftKey ? actif === premier : actif === dernier)) {
      evenement.preventDefault();
      (evenement.shiftKey ? dernier : premier).focus();
    }
  }

  function adressePartage() {
    return window.VISITE_PARTAGE.adresse(window.VISITE_CONFIG?.lienPartage, location.href, parId.get(vueActuelle || DONNEES.start).number);
  }
  function ouvrirPartage(ouvert) {
    $('partage-menu').hidden = !ouvert;
    $('partager').setAttribute('aria-expanded', String(ouvert));
    if (ouvert) {
      ouvrirContacts(false);
      $('partage-message').textContent = '';
      $('partage-repli').hidden = true;
      $('partage-lien').value = adressePartage();
      $('copier-lien').focus({ preventScroll: true });
    }
  }
  async function partagerVue() {
    const url = adressePartage();
    if (matchMedia('(pointer: coarse)').matches && typeof navigator.share === 'function') {
      try { await navigator.share({ title: 'Visite du studio · Hedayat Music', url }); return; }
      catch (erreur) { if (erreur.name === 'AbortError') return; }
    }
    ouvrirPartage($('partage-menu').hidden);
  }
  async function copierLien() {
    const url = adressePartage();
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Copie indisponible');
      await navigator.clipboard.writeText(url);
      $('partage-message').textContent = 'Lien copié';
      $('partage-repli').hidden = true;
    } catch (_) {
      $('partage-message').textContent = 'Sélectionne ce lien pour le copier.';
      $('partage-repli').hidden = false;
      $('partage-lien').value = url;
      $('partage-lien').focus(); $('partage-lien').select();
    }
  }

  /* Le repère le plus proche d'une direction donnée, s'il y en a un à moins de 45°. */
  function repereVers(yaw) {
    const candidat = reperesDe(parId.get(vueActuelle))
      .map(repere => ({ repere, ecart: Math.abs(norm(repere.yaw - yaw)) }))
      .filter(c => c.ecart < 45)
      .sort((a, b) => a.ecart - b.ecart)[0];
    return candidat ? candidat.repere : null;
  }

  /* Clavier. Les flèches tournent le regard, Entrée avance vers le repère le plus en face. */
  function avancerEnFace() {
    const repere = repereVers(lecteur.getYaw());
    if (repere) allerA(repere.cible.id, { garderCap: true, repere });
    else annoncer('Tourne le regard vers un repère au sol, puis appuie sur Entrée.');
  }

  function dessinerAnneau(dessin, contour) {
    const reduire = echelle => contour.replace(/-?\d+(?:\.\d+)?/g, n => (Number(n) * echelle).toFixed(2));
    const chemins = dessin.querySelectorAll('path');
    const limites = [[1, .97], [.97, .94], [.94, .72], [.72, 0]];
    chemins.forEach((p, i) => p.setAttribute('d', reduire(limites[i][0]) + (limites[i][1] ? reduire(limites[i][1]) : '')));
  }

  /* Projection d'un cercle horizontal dans l'image, à partir du rayon de son centre.
     Le rayon physique fait 11 cm ; sa taille à l'écran est bornée pour rester lisible.
     Les centres restent ceux des points de capture, sans décalage vers le haut. */
  function formeDuCercle(yaw, pitch, regard, largeur, hauteur, mur = false) {
    const f = largeur / (2 * Math.tan(enRadians(regard.hfov / 2)));
    const v = enRadians(regard.pitch), a = enRadians(norm(yaw - regard.yaw));
    const distance = mur && pitch > -8 ? 3 : HAUTEUR_CAMERA / Math.max(0.02, Math.abs(Math.tan(enRadians(pitch))));
    const x = distance * Math.sin(a), z = distance * Math.cos(a), y = distance * Math.tan(enRadians(pitch));
    const projeter = (xx, zz, yy = y) => {
      const profondeur = yy * Math.sin(v) + zz * Math.cos(v);
      if (profondeur <= 0.01) return null;
      return [f * xx / profondeur, -f * (yy * Math.cos(v) - zz * Math.sin(v)) / profondeur];
    };
    const centre = projeter(x, z);
    if (!centre) return '';
    const points = Array.from({ length: 32 }, (_, i) => {
      const t = i * Math.PI / 16;
      const inclinaison = mur ? douceur((pitch + 20) / 12) * Math.PI / 2 : 0;
      const u = 0.11 * Math.cos(t), w = 0.11 * Math.sin(t);
      const q = projeter(x + u * Math.cos(a) + w * Math.cos(inclinaison) * Math.sin(a),
        z - u * Math.sin(a) + w * Math.cos(inclinaison) * Math.cos(a), y + w * Math.sin(inclinaison));
      return q && [q[0] - centre[0], q[1] - centre[1]];
    });
    if (points.some(p => !p)) return '';
    // Dans le bas d'un panorama grand-angle, la projection peut redresser
    // visuellement le disque du sol. Garder une ellipse lisible, sans déplacer
    // son centre ni changer le choix de destination. Le mur reste circulaire.
    const largeurProjetee = Math.max(...points.map(p => p[0])) - Math.min(...points.map(p => p[0]));
    const hauteurProjetee = Math.max(...points.map(p => p[1])) - Math.min(...points.map(p => p[1]));
    const poidsSol = mur ? 1 - douceur((pitch + 20) / 12) : 1;
    const aplatissement = 1 - poidsSol * (1 - Math.min(1, .60 * largeurProjetee / Math.max(hauteurProjetee, .01)));
    points.forEach(p => { p[1] *= aplatissement; });
    const diametre = 2 * Math.max(...points.map(p => Math.hypot(...p)));
    const taille = mur
      ? borne(70 * Math.min(1, 3 / distance) * Math.tan(enRadians(50)) / Math.tan(enRadians(regard.hfov / 2)), 40, 84)
      : borne(diametre, 24, 38);
    const echelle = taille / Math.max(diametre, 0.01);
    return points.map((p, i) => (i ? 'L' : 'M') + p.map(v => (v * echelle).toFixed(2)).join(' ')).join(' ') + 'Z';
  }

  /* Le sol est un plan estimé à partir de la hauteur de capture. Sans maillage des murs,
     ne retenir que les liaisons accessibles dans un cône de 45° autour du clic. */
  function choisirPointAuSol(yaw, pitch, points) {
    if (!Number.isFinite(pitch) || !Number.isFinite(yaw) || pitch >= -8) return null;
    const distance = HAUTEUR_CAMERA / Math.max(0.02, Math.abs(Math.tan(enRadians(pitch))));
    return points.map(repere => {
      const ecart = Math.abs(norm(repere.yaw - yaw));
      const d = repere.distance ?? HAUTEUR_CAMERA / Math.max(0.02, Math.abs(Math.tan(enRadians(repere.pitch))));
      return { repere, ecart, score: distance * distance + d * d - 2 * distance * d * Math.cos(enRadians(ecart)) };
    }).filter(c => c.ecart < 45).sort((a, b) => a.score - b.score || a.ecart - b.ecart)[0]?.repere || null;
  }

  // Sans profondeur photographique : normale du sol, puis mur vertical face au regard.
  // Le même passage autorisé est choisi pour le survol et le clic ; jamais à travers un mur sans liaison.
  function surfaceDuRayon(pitch) { return pitch < -14 ? 'sol' : 'mur'; }
  function choisirPointSurface(yaw, pitch, points) {
    if (!Number.isFinite(pitch) || !Number.isFinite(yaw) || pitch > 35) return null;
    if (surfaceDuRayon(pitch) === 'sol') return choisirPointAuSol(yaw, pitch, points);
    return points.map(repere => ({ repere, ecart: Math.abs(norm(repere.yaw - yaw)) }))
      .filter(c => c.ecart < 45).sort((a, b) => a.ecart - b.ecart)[0]?.repere || null;
  }

  // Position conservée même pendant un clic ou un glissement. Le masquage est séparé.
  let sourisSol = null, pointeurAppuye = false;
  const evenementsCurseur = [];
  function noterPointeur(type, e = {}) {
    evenementsCurseur.push({ type, id: e.pointerId, outil: e.pointerType, boutons: e.buttons,
      x: e.clientX, y: e.clientY, lie: !!e.relatedTarget });
    if (evenementsCurseur.length > 12) evenementsCurseur.shift();
  }
  function etatCurseur() {
    const etat = { souris: sourisSol, appuye: pointeurAppuye, deplacement: deplacementEnCours,
      placement, chargee: !!lecteur?.isLoaded(), interface: visite.dataset.etat,
      evenements: evenementsCurseur.slice() };
    const element = sourisSol && document.elementFromPoint(sourisSol.clientX, sourisSol.clientY);
    etat.element = element?.className?.toString();
    const rayon = sourisSol && lecteur?.isLoaded() ? lecteur.mouseEventToCoords(sourisSol) : null;
    etat.rayon = rayon;
    etat.destination = rayon ? choisirPointSurface(rayon[1], rayon[0], reperesActuels)?.cible.number : null;
    etat.raison = !sourisSol ? 'position absente' : pointeurAppuye ? 'appui maintenu' :
      deplacementEnCours ? 'déplacement' : placement !== null ? 'placement' :
      !etat.chargee ? 'chargement' : !element || !$('panorama').contains(element) ? 'commande hors panorama' :
      !etat.destination ? 'direction sans destination' : 'affichable';
    return etat;
  }
  function effacerCurseur() {
    $('curseur-sol').hidden = true;
    for (const bloc of $('panorama').querySelectorAll('.repere-vise')) bloc.classList.remove('repere-vise');
  }
  function actualiserCurseur() {
    effacerCurseur();
    if (!sourisSol || pointeurAppuye || !lecteur || deplacementEnCours || placement !== null || visite.dataset.etat !== 'visite' || !lecteur.isLoaded()) return;
    const cibleDOM = document.elementFromPoint(sourisSol.clientX, sourisSol.clientY);
    if (!cibleDOM || !$('panorama').contains(cibleDOM)) return;
    const [pitch, yaw] = lecteur.mouseEventToCoords(sourisSol);
    const bloc = cibleDOM.closest('.repere:not(.repere-masque)');
    const repere = bloc?._repere || choisirPointSurface(yaw, pitch, reperesActuels);
    if (!repere) return;
    const curseur = $('curseur-sol'), cadre = visite.getBoundingClientRect();
    curseur.hidden = false;
    curseur.style.left = (sourisSol.clientX - cadre.left) + 'px';
    curseur.style.top = (sourisSol.clientY - cadre.top) + 'px';
    curseur.dataset.cible = repere.cible.number;
    curseur.dataset.surface = surfaceDuRayon(pitch);
    dessinerAnneau(curseur.querySelector('svg'), formeDuCercle(yaw, pitch, {
      yaw: lecteur.getYaw(), pitch: lecteur.getPitch(), hfov: lecteur.getHfov()
    }, $('panorama').clientWidth, $('panorama').clientHeight, true));
    // Le cercle qui mène réellement à la destination s'éclaire en même temps.
    for (const point of $('panorama').querySelectorAll('.repere'))
      point.classList.toggle('repere-vise', point.dataset.cible === String(repere.cible.number));
  }

  /* Un geste commun à l'image et aux boutons : mémoriser tout le trajet, pas seulement
     l'écart entre départ et arrivée. Un aller-retour, un pincement ou une annulation ne clique pas. */
  function ecouterClicDansLImage() {
    const zone = $('panorama'), doigts = new Set();
    let geste = null;
    document.addEventListener('pointerdown', e => {
      noterPointeur('down', e);
      doigts.add(e.pointerId);
      pointeurAppuye = true;
      sourisSol = e.pointerType === 'mouse' ? { clientX: e.clientX, clientY: e.clientY } : null;
      effacerCurseur();
      if (doigts.size > 1) { geste = null; sourisSol = null; effacerCurseur(); return; }
      if (!zone.contains(e.target) || e.button !== 0 || e.isPrimary === false || deplacementEnCours) return;
      geste = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), cible: e.target, glisse: false };
    }, true);
    document.addEventListener('pointermove', e => {
      noterPointeur('move', e);
      if (geste && geste.id === e.pointerId && Math.hypot(e.clientX - geste.x, e.clientY - geste.y) > 6) geste.glisse = true;
      sourisSol = e.pointerType === 'mouse' ? { clientX: e.clientX, clientY: e.clientY } : null;
      pointeurAppuye = doigts.size > 0 || !!e.buttons;
      if (sourisSol) actualiserCurseur();
      else effacerCurseur();
    }, true);
    document.addEventListener('pointerup', e => {
      noterPointeur('up', e);
      doigts.delete(e.pointerId);
      pointeurAppuye = doigts.size > 0;
      sourisSol = e.pointerType === 'mouse' ? { clientX: e.clientX, clientY: e.clientY } : null;
      actualiserCurseur();
      const debut = geste;
      if (!debut || debut.id !== e.pointerId) return;
      geste = null;
      if (debut.glisse || doigts.size || performance.now() - debut.t > 500 || Math.hypot(e.clientX - debut.x, e.clientY - debut.y) > 6) return;
      if (!lecteur || !lecteur.isLoaded() || deplacementEnCours || placement !== null || visite.dataset.etat !== 'visite') return;
      const cibleDOM = document.elementFromPoint(e.clientX, e.clientY);
      if (!cibleDOM || !zone.contains(cibleDOM)) return;
      const bloc = debut.cible.closest('.repere');
      if (bloc && (bloc.classList.contains('repere-masque') || !bloc.contains(cibleDOM))) return;
      const [pitch, yaw] = lecteur.mouseEventToCoords(e);
      const repere = bloc?._repere || choisirPointSurface(yaw, pitch, reperesActuels);
      if (repere) allerA(repere.cible.id, { garderCap: true, repere });
    }, true);
    document.addEventListener('pointercancel', e => { noterPointeur('cancel', e); doigts.delete(e.pointerId); pointeurAppuye = doigts.size > 0; geste = null; sourisSol = null; effacerCurseur(); }, true);
    document.addEventListener('pointerout', e => { noterPointeur('out', e); if (!e.relatedTarget) { sourisSol = null; effacerCurseur(); } });
    window.addEventListener('blur', () => { noterPointeur('blur'); doigts.clear(); pointeurAppuye = false; geste = null; sourisSol = null; effacerCurseur(); });
  }

  function surTouche(evenement) {
    if (evenement.defaultPrevented || evenement.metaKey || evenement.ctrlKey || evenement.altKey) return;
    if (evenement.key === 'Escape') {
      if ($('infos').open) return; // le dialogue gère Échap et rend le focus
      if (!$('partage-menu').hidden) { ouvrirPartage(false); $('partager').focus({ preventScroll: true }); return; }
      if (!$('contacts').hidden) { ouvrirContacts(false); $('contacts-ouvrir').focus({ preventScroll: true }); return; }
      if (placement !== null) { arreterPlacement(); return; }
      if (!$('plan').hidden) { ouvrirPlan(false); $('plan-ouvrir').focus({ preventScroll: true }); return; }
      if (!$('espaces-panneau').hidden) { ouvrirEspaces(false); $('espaces-ouvrir').focus({ preventScroll: true }); return; }
      if (MODE_TRAVAIL && !$('atelier').hidden) { ouvrirAtelier(false); return; }
      return;
    }
    const cible = evenement.target;
    if (cible.closest('input, textarea, select, dialog, [contenteditable]')) return;
    if (!lecteur || visite.dataset.etat !== 'visite') return;
    const surBouton = cible.closest('button, a, [role="button"]');
    const pas = evenement.shiftKey ? 30 : 12;
    switch (evenement.key) {
      case 'ArrowLeft': viser(-pas, 0, 0); break;
      case 'ArrowRight': viser(pas, 0, 0); break;
      case 'ArrowUp': viser(0, pas * 0.7, 0); break;
      case 'ArrowDown': viser(0, -pas * 0.7, 0); break;
      case '+': case '=': zoomer(1); break;
      case '-': case '_': zoomer(-1); break;
      case 'Enter': if (surBouton) return; avancerEnFace(); break;
      case 'r': case 'R': case 'Home': recentrer(); break;
      case 'p': case 'P': ouvrirPlan($('plan').hidden); break;
      case 'e': case 'E': ouvrirEspaces(panneauEspacesPermanent() || $('espaces-panneau').hidden); break;
      case 'f': case 'F': basculerPleinEcran(); break;
      default: return;
    }
    evenement.preventDefault();
  }

  /* ---------------------------------------------------------------- 8. Atelier (mode travail) */

  function afficherEtatEnregistrement(vientDEnregistrer) {
    if (!MODE_TRAVAIL) return;
    const modifiees = Object.keys(indications).length;
    const aActualiser = Object.values(indications).filter(fiche => fiche.needsUpdate).length;
    const compte = modifiees === 0 ? 'Aucune vue modifiée pour l’instant.'
      : (modifiees === 1 ? '1 vue modifiée' : modifiees + ' vues modifiées') +
        (aActualiser ? ', dont ' + aActualiser + ' à actualiser.' : '.');
    let phrase;
    if (!stockageDisponible) phrase = 'Le stockage de ce navigateur est fermé : exporte tes indications pour les conserver. ';
    else if (vientDEnregistrer) {
      const heure = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }).replace(':', ' h ');
      phrase = 'Enregistré dans ce navigateur à ' + heure + '. ';
    } else phrase = 'Tes indications restent dans ce navigateur, sur cet ordinateur. ';
    $('atelier-etat-texte').textContent = phrase + compte + ' Exporte-les pour les garder dans un fichier.';
  }

  function ficheActuelle() {
    if (!indications[vueActuelle]) indications[vueActuelle] = {};
    return indications[vueActuelle];
  }

  function remplirAtelier() {
    const point = parId.get(vueActuelle);
    const fiche = indications[point.id] || {};
    $('atelier-vue').textContent = 'Vue ' + point.number + ' · ' + insecable(point.label);
    $('champ-nom').value = fiche.label || point.label;
    $('champ-note').value = fiche.note || '';
    $('champ-actualiser').checked = !!fiche.needsUpdate;
    $('angle-etat').textContent = fiche.angle
      ? 'Angle réglé à la main : ' + Math.round(fiche.angle.yaw) + '° de côté, ' + Math.round(fiche.angle.pitch) + '° de hauteur.'
      : 'Angle d’origine.';
    $('choix-vue').replaceChildren(...DONNEES.locations.slice().sort((a, b) => a.number - b.number).map(p => {
      const option = el('option', '', 'Vue ' + p.number + ' · ' + nomDe(p) + (indications[p.id] ? ' · modifiée' : ''));
      option.value = p.id;
      option.selected = p.id === point.id;
      return option;
    }));
    const affiches = new Map(reperesDe(point).map(repere => [repere.cible.number, repere]));
    const reglages = fiche.links || {};
    const lignes = point.neighbors.map(id => parId.get(id)).sort((a, b) => a.number - b.number).map(cible => {
      const ligne = el('li');
      const etiquette = el('label', 'case');
      const coche = el('input');
      coche.type = 'checkbox';
      coche.checked = affiches.has(cible.number);
      coche.addEventListener('change', () => reglerRepere(cible.number, coche.checked));
      etiquette.append(coche, document.createTextNode(' Vue ' + cible.number + ' · ' + insecable(nomDe(cible))));
      const placer = el('button', 'bouton mini', placement === cible.number ? 'Clique dans l’image' : 'Placer');
      placer.type = 'button';
      placer.setAttribute('aria-pressed', String(placement === cible.number));
      placer.addEventListener('click', () => placement === cible.number ? arreterPlacement() : commencerPlacement(cible.number));
      ligne.append(etiquette, placer);
      if (Number.isFinite(reglages[cible.number]?.yaw)) ligne.append(el('span', 'marque', 'placé à la main'));
      else if (reglages[cible.number]) ligne.append(el('span', 'marque', 'modifié'));
      return ligne;
    });
    $('liste-reperes').replaceChildren(...lignes);
    afficherEtatEnregistrement(false);
  }

  function reglerRepere(numero, affiche) {
    const point = parId.get(vueActuelle);
    const fiche = ficheActuelle();
    const liens = fiche.links || (fiche.links = {});
    const prevuAuCatalogue = (point.links || []).some(lien => lien.to === numero);
    if (affiche === prevuAuCatalogue) delete liens[numero];              // retour au réglage d'origine
    else liens[numero] = affiche ? { affiche: true } : { masque: true };
    persister();
    poserReperes(point);
    afficherVue();
  }

  function commencerPlacement(numero) {
    placement = numero;
    visite.classList.add('placement');
    annoncer('Clique dans l’image, à l’endroit où poser le repère vers la vue ' + numero + '. Échap pour annuler.');
    remplirAtelier();
  }
  function arreterPlacement() {
    placement = null;
    visite.classList.remove('placement');
    remplirAtelier();
  }

  /* Pose le repère à l'endroit cliqué. On ignore un clic qui termine un glissement. */
  function ecouterPlacement() {
    const zone = $('panorama');
    let depart = null;
    zone.addEventListener('pointerdown', evenement => { depart = [evenement.clientX, evenement.clientY]; }, true);
    zone.addEventListener('pointerup', evenement => {
      if (placement === null || !depart || !lecteur) return;
      if (Math.hypot(evenement.clientX - depart[0], evenement.clientY - depart[1]) > 6) return;
      const [pitch, yaw] = lecteur.mouseEventToCoords(evenement);
      const fiche = ficheActuelle();
      (fiche.links || (fiche.links = {}))[placement] = { yaw: Math.round(yaw * 10) / 10, pitch: Math.round(pitch * 10) / 10 };
      const numero = placement;
      placement = null;
      visite.classList.remove('placement');
      persister();
      poserReperes(parId.get(vueActuelle));
      afficherVue();
      annoncer('Repère vers la vue ' + numero + ' posé.');
    }, true);
  }

  function lireFormulaire() {
    const point = parId.get(vueActuelle);
    const fiche = ficheActuelle();
    fiche.label = $('champ-nom').value.trim() || point.label;
    fiche.note = $('champ-note').value;
    fiche.needsUpdate = $('champ-actualiser').checked;
  }

  let minuterieSaisie;
  function surSaisie() {
    lireFormulaire();
    clearTimeout(minuterieSaisie);
    minuterieSaisie = setTimeout(() => {
      persister();
      afficherEntete();     // le formulaire n'est pas réécrit : le curseur reste où il est pendant la saisie
      afficherEspaces();
    }, 400);
  }

  function ouvrirAtelier(ouvert) {
    $('atelier').hidden = !ouvert;
    $('atelier-ouvrir').setAttribute('aria-expanded', String(ouvert));
    visite.classList.toggle('atelier-ouvert', ouvert);
    if (ouvert) { remplirAtelier(); $('champ-nom').focus({ preventScroll: true }); }
    else { arreterPlacement(); $('atelier-ouvrir').focus({ preventScroll: true }); }
    if (lecteur) lecteur.resize();
  }

  function exporter() {
    lireFormulaire();
    persister();
    const contenu = { version: 2, modelId: CONFIG.modele, exportedAt: new Date().toISOString(), edits: indications };
    const adresse = URL.createObjectURL(new Blob([JSON.stringify(contenu, null, 2)], { type: 'application/json' }));
    const lien = el('a');
    lien.href = adresse;
    lien.download = 'hedayat-music-indications-' + new Date().toISOString().slice(0, 10) + '.json';
    document.body.append(lien);
    lien.click();
    lien.remove();
    setTimeout(() => URL.revokeObjectURL(adresse), 1500);
    annoncer('Fichier d’indications exporté dans tes téléchargements.');
  }

  async function importer(fichier) {
    try {
      if (fichier.size > 1000000) throw new Error('fichier trop lourd');
      const contenu = JSON.parse(await fichier.text());
      if (![1, 2].includes(contenu.version) || contenu.modelId !== CONFIG.modele || !contenu.edits) throw new Error('autre visite');
      const recues = nettoyer(contenu.edits);
      if (Object.keys(indications).length &&
          !confirm('Remplacer les indications de ce navigateur par celles du fichier ?')) return;
      appliquerIndications(recues);
      const nombre = Object.keys(recues).length;
      annoncer(nombre === 1 ? 'Indications réimportées : 1 vue.' : 'Indications réimportées : ' + nombre + ' vues.');
    } catch (erreur) {
      annoncer('Ce fichier vient d’une autre visite ou d’un autre format. Choisis un export de cette visite.');
    }
  }

  /* Remplace toutes les indications, puis remet l'affichage d'accord avec elles. */
  function appliquerIndications(nouvelles) {
    indications = nouvelles;
    persister();
    if (lecteur) DONNEES.locations.forEach(poserReperes);
    afficherVue();
  }

  function brancherAtelier() {
    $('bandeau-travail').hidden = false;
    visite.classList.add('mode-travail');
    $('quitter-travail').href = location.pathname + location.hash;
    $('atelier-ouvrir').addEventListener('click', () => ouvrirAtelier($('atelier').hidden));
    $('atelier-fermer').addEventListener('click', () => ouvrirAtelier(false));
    $('choix-vue').addEventListener('change', evenement => allerA(evenement.target.value));
    for (const champ of ['champ-nom', 'champ-note', 'champ-actualiser']) {
      $(champ).addEventListener('input', surSaisie);
      $(champ).addEventListener('change', surSaisie);
    }
    $('angle-garder').addEventListener('click', () => {
      ficheActuelle().angle = { yaw: lecteur.getYaw(), pitch: lecteur.getPitch(), hfov: lecteur.getHfov() };
      persister();
      remplirAtelier();
      annoncer('Cet angle sera repris à chaque ouverture de cette vue.');
    });
    $('angle-origine').addEventListener('click', () => {
      delete ficheActuelle().angle;
      persister();
      remplirAtelier();
      recentrer();
    });
    $('reperes-origine').addEventListener('click', () => {
      delete ficheActuelle().links;
      persister();
      poserReperes(parId.get(vueActuelle));
      afficherVue();
      annoncer('Repères d’origine remis sur cette vue.');
    });
    $('indications-enregistrer').addEventListener('click', () => {
      lireFormulaire();
      const reussi = persister();
      afficherVue();
      annoncer(reussi ? 'Indications enregistrées dans ce navigateur.' : 'Stockage fermé : exporte tes indications pour les conserver.');
    });
    $('indications-exporter').addEventListener('click', exporter);
    $('indications-importer').addEventListener('change', evenement => {
      const fichier = evenement.target.files[0];
      if (fichier) importer(fichier);
      evenement.target.value = '';
    });
    $('indications-effacer').addEventListener('click', () => {
      if (!confirm('Effacer toutes les indications enregistrées dans ce navigateur ? Un export déjà fait reste intact.')) return;
      appliquerIndications({});
      annoncer('Indications effacées de ce navigateur.');
    });
    ecouterPlacement();
  }

  /* ---------------------------------------------------------------- 9. Mise en route */

  function vueDeLAdresse() {
    const lien = /^#point-(\d+)$/.exec(location.hash);
    return lien && parNumero.has(Number(lien[1])) ? parNumero.get(Number(lien[1])).id : null;
  }

  function initialiser() {
    actualiserCommerce();
    $('contacts-ouvrir').addEventListener('click', () => ouvrirContacts($('contacts').hidden));
    new ResizeObserver(() => {
      visite.style.setProperty('--commerce-hauteur', $('commerce').getBoundingClientRect().height + 'px');
    }).observe($('commerce'));
    new ResizeObserver(() => { if (lecteur) lecteur.resize(); }).observe($('panorama'));
    const depart = parId.get(DONNEES.start);
    const demandee = vueDeLAdresse();
    $('accueil-affiche-portrait').srcset = urlAffiche(depart, true);
    $('accueil-affiche').src = urlAffiche(depart);

    $('commencer').addEventListener('click', () => demarrer(DONNEES.start));
    $('retour-depart').addEventListener('click', evenement => { evenement.preventDefault(); allerA(DONNEES.start); });
    $('zoom-plus').addEventListener('click', () => zoomer(1));
    $('zoom-moins').addEventListener('click', () => zoomer(-1));
    $('recentrer').addEventListener('click', recentrer);
    $('plein-ecran').addEventListener('click', basculerPleinEcran);
    $('infos-ouvrir').addEventListener('click', () => ouvrirInfos(true));
    $('infos-reprendre').addEventListener('click', () => ouvrirInfos(false));
    $('infos').addEventListener('keydown', garderFocusDansAide);
    $('infos').addEventListener('close', () => $('infos-ouvrir').focus({ preventScroll: true }));
    $('partager').addEventListener('click', partagerVue);
    $('copier-lien').addEventListener('click', copierLien);
    $('infos-fermer').addEventListener('click', () => ouvrirInfos(false));
    $('infos').addEventListener('click', evenement => { if (evenement.target === $('infos')) ouvrirInfos(false); });
    $('plan-ouvrir').addEventListener('click', () => ouvrirPlan($('plan').hidden));
    $('espaces-ouvrir').addEventListener('click', () => ouvrirEspaces(panneauEspacesPermanent() || $('espaces-panneau').hidden));
    $('espaces-fermer').addEventListener('click', () => { ouvrirEspaces(false); $('espaces-ouvrir').focus({ preventScroll: true }); });
    $('chemin').addEventListener('click', montrerLeChemin);
    $('plan-fermer').addEventListener('click', () => { ouvrirPlan(false); $('plan-ouvrir').focus({ preventScroll: true }); });
    $('alerte-relancer').addEventListener('click', () => {
      masquerAlerte();
      if (derniereDemande) allerA(derniereDemande[0], derniereDemande[1]);
    });
    $('alerte-depart').addEventListener('click', () => { masquerAlerte(); allerA(DONNEES.start); });
    document.addEventListener('keydown', surTouche);
    ecouterClicDansLImage();
    window.addEventListener('hashchange', () => {
      const identifiant = vueDeLAdresse();
      if (identifiant && identifiant !== vueActuelle) allerA(identifiant);
    });
    preparerPleinEcran();
    matchMedia('(min-width: 1024px) and (pointer: fine)').addEventListener('change', e => {
      if (!e.matches) ouvrirEspaces(false);
    });

    afficherEspaces();

    if (CONFIG.modeTravail === 'jamais') { $('atelier').remove(); $('bandeau-travail').remove(); }
    else if (MODE_TRAVAIL) brancherAtelier();

    // Un lien direct, le mode travail ou ?direct ouvrent la visite sans passer par l'écran d'accueil.
    if (!CONFIG.ecranAccueil || demandee || MODE_TRAVAIL || parametres.has('direct')) demarrer(demandee || DONNEES.start);
  }

  /* Petite porte d'entrée pour les contrôles automatiques et le dépannage, depuis la console du navigateur :
     visite360.aller(25) ouvre la vue 25, visite360.etat() dit où l'on est. */
  window.visite360 = {
    aller: (numero, options) => parNumero.has(numero) ? allerA(parNumero.get(numero).id, options) : undefined,
    etat: () => ({
      etat: visite.dataset.etat,
      vue: vueActuelle ? parId.get(vueActuelle).number : null,
      nom: vueActuelle ? nomDe(parId.get(vueActuelle)) : null,
      chargee: !!lecteur && lecteur.isLoaded() && !deplacementEnCours,
      yaw: lecteur ? lecteur.getYaw() : null,
      pitch: lecteur ? lecteur.getPitch() : null,
      hfov: lecteur ? lecteur.getHfov() : null,
      reperes: vueActuelle ? reperesDe(parId.get(vueActuelle)).map(repere => repere.cible.number) : [],
      points: reperesActuels.map(r => ({ numero: r.cible.number, yaw: r.yaw, pitch: r.pitch })),
      curseur: etatCurseur(),
      deplacement: deplacementEnCours,
      transition: transitionVisite ? { prete: transitionVisite.prete !== null, ...progressionTransition(transitionVisite.debut, transitionVisite.prete, performance.now()) } : null,
      chemin: !$('chemin').hidden,
      modeTravail: MODE_TRAVAIL,
      alerte: !$('alerte').hidden,
      indications: JSON.parse(JSON.stringify(indications))
    }),
    regarder: (yaw, pitch, hfov) => {
      if (!lecteur) return;
      // Une visée explicite remplace aussi les animations de recentrage déjà lancées.
      lecteur.stopMovement();
      visee.instant = 0;
      lecteur.lookAt(pitch ?? lecteur.getPitch(), yaw, hfov ?? lecteur.getHfov(), false);
      actualiserCurseur();
    }
  };

  initialiser();
})();
